import { Fragment, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardMedia,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import html2canvas from "html2canvas";
import { useApp } from "../../../../hooks/useApp";
import { useStorage } from "../../../../hooks/useStorage";
import Gobutton from "../../../../components/Gobutton";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import FactoryIcon from "@mui/icons-material/Factory";
import InfoIcon from "@mui/icons-material/Info";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import Videoplayer from "../../../../components/Videoplayer";
import SvgIcon from "@mui/material/SvgIcon";
import { ReactComponent as TulipLibraryWhite } from "../../../../svgs/tulipLibraryWhite.svg";
import { ReactComponent as TulipLibrary } from "../../../../svgs/tulipLibrary.svg";

const style = {
  p: 0,
  width: "100%",
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: "#FFF",
  fontSize: "6px",
};

export default function StepThree({ asset, setAsset }) {
  const [htmlFile, setHtmlFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const screenshotIframeRef = useRef(null);
  const { uploadFile } = useStorage();

  function handleChange(event) {
    const newAsset = { ...asset, [event.target.id]: event.target.value };
    setAsset(newAsset);
  }

  const handleHtmlFileChange = (event) => {
    setHtmlFile(event.target.files[0]);
  };

  const captureScreenshot = async (htmlContent) => {
    return new Promise((resolve) => {
      const iframe = screenshotIframeRef.current;
      if (!iframe) { resolve(null); return; }

      iframe.srcdoc = htmlContent;
      iframe.onload = async () => {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

          // Wait for fonts to finish loading
          if (iframeDoc.fonts && iframeDoc.fonts.ready) {
            await iframeDoc.fonts.ready;
          }

          // Wait for layout and paint to settle
          await new Promise((r) => {
            requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, 500)));
          });

          const canvas = await html2canvas(iframeDoc.body, {
            width: 1280,
            height: 720,
            windowWidth: 1280,
            windowHeight: 720,
            useCORS: true,
            scale: 1,
          });
          canvas.toBlob(async (blob) => {
            if (!blob) { resolve(null); return; }
            const file = new File([blob], `html-app-thumb-${Date.now()}.png`, { type: "image/png" });
            const imageUrl = await uploadFile(file, "assets");
            resolve(imageUrl);
          }, "image/png");
        } catch (err) {
          console.error("Screenshot capture failed:", err);
          resolve(null);
        }
      };
    });
  };

  const handleHtmlUpload = async () => {
    if (!htmlFile) return;
    setUploading(true);
    try {
      const htmlContent = await htmlFile.text();
      const url = await uploadFile(htmlFile, "html-apps");
      const updates = { ...asset, htmlUrl: url };

      // Auto-capture screenshot as thumbnail for both Image (card) and img (detail)
      const screenshotUrl = await captureScreenshot(htmlContent);
      if (screenshotUrl) {
        if (!updates.Image) updates.Image = screenshotUrl;
        if (!updates.img) updates.img = screenshotUrl;
      }

      setAsset(updates);
    } catch (error) {
      console.error("HTML upload failed:", error);
    }
    setUploading(false);
  };

  const { activeVideo } = useApp();

  return (
    <Grid container sx={{ flexDirection: "row", gap: 4 }}>
      {activeVideo && <Videoplayer />}
      <iframe
        ref={screenshotIframeRef}
        title="screenshot-capture"
        style={{ position: "fixed", top: 0, left: 0, width: "1280px", height: "720px", border: "none", visibility: "hidden", pointerEvents: "none", zIndex: -1 }}
      />
      <Grid
        item
        xs={12}
        sm={6}
        md={7}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        <Grid
          item
          xs
          sx={{ backgroundColor: "background.default" }}
          marginY={"auto"}
          borderRadius={"0px 8px 8px 8px"}
          padding={5}
        >
          <Grid container gap={2}>
            <Grid item xs={12}>
              <Typography color={"customColors.default"} variant="h8">
                {asset.Title}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} alignItems={"center"}>
              <Card sx={{ boxShadow: "none", backgroundColor: "transparent" }}>
                <CardMedia
                  component="img"
                  image={asset.img}
                  style={{
                    boxShadow: "none",
                    maxWidth: "100%", // Set width to 100% to fill the entire width of the Card
                    maxHeight: "80vh",
                    objectFit: "cover", // Maintain aspect ratio while covering the entire space
                    objectPosition: "center", // Center the image within the CardMedia
                  }}
                />
              </Card>
            </Grid>
            <Grid item xs>
              <Typography fontSize={"small"} color={"customColors.default"}>
                Key Features
              </Typography>
              {asset.Features && asset.Features[0] !== "" && (
                <List aria-label="mailbox folders" sx={style}>
                  {asset.Features.map((feature, index) => (
                    <Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          sx={{ color: "customColors.defaultLight" }}
                          primary={feature}
                        />
                      </ListItem>
                      {index < asset.Features.length - 1 && (
                        <Divider component="li" />
                      )}
                    </Fragment>
                  ))}
                </List>
              )}
            </Grid>
            <Grid
              container
              justifyContent={"right"}
              alignItems={"bottom"}
              gap={2}
            >
              {asset.appUrl && (
                <Gobutton
                  text={"Open the App"}
                  startIcon={<ExitToAppIcon />}
                  url={{ type: "beacon", url: asset.appUrl }}
                />
              )}
              {asset.editorUrl && (
                <Gobutton
                  text={"Open the Editor"}
                  startIcon={<LaptopMacIcon />}
                  url={{ type: "beacon", url: asset.editorUrl }}
                />
              )}
              {asset.contentUrl && (
                <Gobutton
                  text={"More info"}
                  startIcon={<InfoIcon />}
                  url={{ type: "content", url: asset.contentUrl }}
                />
              )}
              {asset.video && (
                <Gobutton
                  text={"Play the Video"}
                  startIcon={<InfoIcon />}
                  url={{ type: "video", url: asset.video }}
                />
              )}
              {asset.tulipLibrary && (
                <Gobutton
                  text={"Open in Tulip Library"}
                  startIcon={
                    <SvgIcon 
                      className="tulipLibrary"
                      inheritViewBox
                      sx={{
                        height: "18px",
                        width: "auto",
                        color: "customColors.white",
                      }}
                      component={TulipLibraryWhite}
                    />
                  }
                  url={{ type: "beacon", url: asset.tulipLibrary }}
                />
              )}
              {asset.htmlUrl && (
                <Gobutton
                  text={"Preview HTML App"}
                  startIcon={<WebAssetIcon />}
                  url={{ type: "mockpc", url: asset.htmlUrl }}
                />
              )}
              {asset.Type === "factory" && (
                <Gobutton
                  text={"Explore the Factory"}
                  startIcon={<FactoryIcon />}
                  url={"factory"}
                />
              )}
              {asset.Type === "megafactory" && (
                <Gobutton
                  text={"Explore the Factory"}
                  startIcon={<FactoryIcon />}
                  url={"megafactory"}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs>
        <Stack direction={"column"} gap={4}>
          <TextField
            id="appUrl"
            label="Open the App"
            fullWidth
            value={asset.appUrl}
            onChange={(event) => {
              handleChange(event);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ExitToAppIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="editorUrl"
            label="Open the Editor"
            fullWidth
            value={asset.editorUrl}
            onChange={(event) => {
              handleChange(event);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LaptopMacIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="contentUrl"
            label="More Info"
            fullWidth
            value={asset.contentUrl}
            onChange={(event) => {
              handleChange(event);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InfoIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="video"
            label="Play Video"
            fullWidth
            value={asset.video}
            onChange={(event) => {
              handleChange(event);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InfoIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="tulipLibrary"
            label="Open in Tulip Library"
            fullWidth
            value={asset.tulipLibrary}
            onChange={(event) => {
              handleChange(event);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon
                    inheritViewBox
                    sx={{
                      height: "18px",
                      width: "auto",
                      color: "customColors.white",
                    }}
                    component={TulipLibrary}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            border="1px solid #ccc"
            borderRadius="8px"
            p={2}
          >
            <Typography variant="subtitle2" color="customColors.default">
              HTML App Upload
            </Typography>
            <Box display="flex" flexDirection="row" gap={2} alignItems="center">
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFileIcon />}
              >
                Choose HTML File
                <input
                  type="file"
                  hidden
                  accept=".html,.htm"
                  onChange={handleHtmlFileChange}
                />
              </Button>
              {htmlFile && (
                <Typography variant="body2" color="textSecondary">
                  {htmlFile.name}
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleHtmlUpload}
                disabled={uploading || !htmlFile}
                startIcon={uploading ? <CircularProgress size={20} /> : null}
              >
                Upload
              </Button>
            </Box>
            {asset.htmlUrl && (
              <TextField
                id="htmlUrl"
                label="HTML App URL"
                fullWidth
                value={asset.htmlUrl}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WebAssetIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
}
